import { HLAlert, useHLNotification } from '@platform-ui/highrise'

import { h } from 'vue'

export type HLNotificationApi = ReturnType<typeof useHLNotification>

let notificationInstance: HLNotificationApi | null = null

export const initNotification = (notification: HLNotificationApi) => {
  notificationInstance = notification || useHLNotification()
}

export const clearNotification = () => {
  notificationInstance = null
}

export const getNotification = () => {
  if (!notificationInstance) {
    console.warn('HLNotification instance is not ready yet.')
    return null
  }

  return notificationInstance
}

type ToastInput =
  | string
  | {
      title?: string
      description?: string
      // Back-compat: also accept `content` and `subtitle` as aliases
      content?: string
      subtitle?: string
    }

type ToastOptions = Record<string, unknown>

// Default titles when the caller passes only a message string.
// Keeps every toast title-first so the visual hierarchy reads like
// "Success" / "Could not save changes" rather than a wall of body text.
const DEFAULT_TITLES = {
  success: 'Success',
  error: 'Something went wrong',
  warning: 'Heads up',
  info: 'Info',
  toast: 'Notification',
} as const

type ToastKind = keyof typeof DEFAULT_TITLES

const COLORS: Record<ToastKind, string> = {
  success: 'green',
  error: 'red',
  warning: 'orange',
  info: 'blue',
  toast: 'gray',
}

const DURATIONS: Record<ToastKind, number> = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 3000,
  toast: 3000,
}

const resolveContent = (
  input: ToastInput,
  kind: ToastKind,
): { title: string; content?: string } => {
  if (typeof input === 'string') {
    return { title: DEFAULT_TITLES[kind], content: input }
  }

  const description = input.description ?? input.content ?? input.subtitle
  return {
    title: input.title ?? DEFAULT_TITLES[kind],
    content: description,
  }
}

const showToast = (
  kind: ToastKind,
  input: ToastInput,
  options: ToastOptions = {},
) => {
  const { title, content } = resolveContent(input, kind)
  const toast = getNotification()?.create({
    content: () =>
      h(HLAlert, {
        ...options,
        id: `${kind}-toast`,
        class: 'kaizen-toast-alert',
        type: 'notification',
        color: COLORS[kind],
        // Lock width via prop so HighRise drives it through its own
        // --alert-width / --alert-max-width CSS vars rather than us
        // overriding library classes.
        width: '380px',
        maxWidth: '380px',
        title,
        content,
        closable: true,
        onClose: () => {
          toast?.destroy()
        },
      }),
    duration: DURATIONS[kind],
  })
}

export function useToast() {
  return {
    success: (message: ToastInput, options: ToastOptions = {}) =>
      showToast('success', message, options),
    error: (message: ToastInput, options: ToastOptions = {}) =>
      showToast('error', message, options),
    warning: (message: ToastInput, options: ToastOptions = {}) =>
      showToast('warning', message, options),
    info: (message: ToastInput, options: ToastOptions = {}) =>
      showToast('info', message, options),
    toast: (message: ToastInput, options: ToastOptions = {}) =>
      showToast('toast', message, options),
  }
}
